import React, { useEffect, useState } from 'react';
import { ImageOff, Images } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { loadAuthenticatedMedia } from '@/api/authenticatedMedia';
import { API } from '@/config/constants';

function SavedPreview({ item, index, owner }) {
  const [preview, setPreview] = useState({ state: 'loading' });
  const [retry, setRetry] = useState(0);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    let objectUrl;
    setPreview({ state: 'loading', item, opening: owner.opening });
    setExpanded(false);
    if (item.valid && owner.owns()) {
      const token = owner.getToken();
      if (token && owner.owns()) {
        loadAuthenticatedMedia(`${API}/delivery-media/${encodeURIComponent(item.path)}`, {
          signal: controller.signal, headers: { Authorization: `Bearer ${token}` },
        }).then((result) => {
          if (controller.signal.aborted || !owner.owns()) return;
          if (result.blob) objectUrl = URL.createObjectURL(result.blob);
          setPreview({ state: result.state, url: objectUrl, item, opening: owner.opening });
        }).catch(() => {
          if (!controller.signal.aborted && owner.owns()) setPreview({ state: 'error' });
        });
      } else setPreview({ state: 'forbidden' });
    } else setPreview({ state: 'unavailable' });
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
    // The opening and exact metadata item own this resource, not render-time
    // callback identities. All asynchronous exits also check current authority.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, owner.opening, retry]);
  const failed = () => setPreview((current) => ({ ...current, state: 'error' }));
  const available = preview.item === item && preview.opening === owner.opening && preview.state === 'available' && preview.url;
  const previewMessage = preview.state === 'loading' ? 'Loading preview…'
    : preview.state === 'forbidden' ? 'Not authorized to view this preview. The saved record is still present.'
      : preview.state === 'error' ? 'Could not load this preview. Retry to try again; the saved record is still present.'
        : 'Preview unavailable. The saved record is still present.';
  return (
    <div className="space-y-2 border rounded-lg p-3 min-w-0">
      <p className="text-sm font-medium">Saved {item.type === 'video' ? 'video' : item.type === 'photo' ? 'photo' : 'media'} {index + 1}</p>
      {available ? item.type === 'video' ? (
        <video controls preload="metadata" src={preview.url} onError={failed} className="max-h-48 w-full" data-testid={`saved-media-video-${index}`} />
      ) : (
        <button type="button" onClick={() => setExpanded(true)} className="block" aria-label={`View saved photo ${index + 1}`} data-testid={`saved-media-view-${index}`}>
          <img src={preview.url} alt={`Saved delivery photo ${index + 1}`} onError={failed} className="max-h-48 max-w-full object-contain rounded" data-testid={`saved-media-photo-${index}`} />
        </button>
      ) : <p role="status" className="text-sm text-stone-600 flex gap-2"><ImageOff className="h-4 w-4 shrink-0" />{previewMessage}</p>}
      {item.valid && !available && preview.state !== 'loading' && <Button type="button" variant="outline" onClick={() => setRetry((value) => value + 1)} data-testid={`saved-media-preview-retry-${index}`}>Retry preview</Button>}
      {!item.valid && <p className="text-xs text-stone-500">This saved item cannot be managed here because its media details are unsupported or incomplete.</p>}
      {item.valid && <Button type="button" variant="outline" disabled={!owner.canRemove} onClick={() => owner.selectRemoval(item)} data-testid={`saved-media-remove-${index}`}>Remove saved {item.type}</Button>}
      <Dialog open={expanded && !!available} onOpenChange={setExpanded}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Saved delivery photo {index + 1}</DialogTitle><DialogDescription>Previously saved proof of delivery.</DialogDescription></DialogHeader>
          {available && <img src={preview.url} alt={`Saved delivery photo ${index + 1}, expanded`} onError={failed} className="max-h-[75vh] w-full object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function SavedDeliveryMedia({ media }) {
  if (!media) return null;
  return (
    <section className="p-4 space-y-3" data-testid="saved-delivery-media" aria-label="Saved delivery media">
      <h2 className="text-sm font-semibold">Saved delivery media</h2>
      <p className="text-xs text-stone-600">To replace proof, add and save the new upload first, then reopen and remove the old item. Saved-media removal takes effect immediately after confirmation.</p>
      {media.offline && <p className="text-sm text-amber-900">Connect and sign in to the cloud to remove saved media. Offline removals are not queued.</p>}
      {media.status === 'loading' && <p role="status">Loading saved delivery media…</p>}
      {media.message && <p role="status" className="text-sm text-amber-900">{media.message}</p>}
      {media.status === 'ready' && !media.items.length && <p className="flex gap-2 text-sm text-stone-500"><Images className="h-4 w-4" />No saved delivery photos or videos.</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {media.items.map((item, index) => <SavedPreview key={`${item.key}:${item.path}`} item={item} index={index} owner={media} />)}
      </div>
      <Button type="button" variant="outline" disabled={!media.canRefresh} onClick={media.refresh} data-testid="saved-media-refresh">
        {media.needsReview ? 'Review current delivery / retry refresh only' : 'Refresh saved media'}
      </Button>
      <Dialog open={!!media.selected} onOpenChange={(open) => { if (!open) media.cancelRemoval(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove saved {media.selected?.type}?</DialogTitle>
            <DialogDescription>This removes the selected saved item immediately and records the removal in delivery history. Closing the form will not undo it. Unsaved notes and new uploads are not submitted by this action.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={media.cancelRemoval} data-testid="saved-media-cancel-removal">Cancel</Button>
            <Button type="button" variant="destructive" disabled={!media.canRemove} onClick={media.confirmRemoval} data-testid="saved-media-confirm-removal">Remove saved {media.selected?.type} now</Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
